/**
 * Input Component
 * Accessible form input with validation states
 */

import React from 'react';
import styles from './Input.module.css';

export type InputType = 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'date' | 'time' | 'search';
export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input type */
  type?: InputType;
  /** Input size */
  size?: InputSize;
  /** Label text */
  label?: string;
  /** Helper text below input */
  helperText?: string;
  /** Error message (shows error state if present) */
  error?: string;
  /** Whether field is required */
  isRequired?: boolean;
  /** Left icon/element */
  iconLeft?: React.ReactNode;
  /** Right icon/element */
  iconRight?: React.ReactNode;
  /** Full width */
  isFullWidth?: boolean;
}

/**
 * Input Component
 *
 * @example
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   isRequired
 * />
 *
 * @example
 * <Input
 *   label="Name"
 *   error="Name is required"
 *   helperText="Please enter your full name"
 * />
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      size = 'md',
      label,
      helperText,
      error,
      isRequired = false,
      iconLeft,
      iconRight,
      isFullWidth = false,
      disabled = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;

    // Build input classes
    const inputClasses = `${styles.input} ${hasError ? styles.error : ''} ${
      iconLeft ? styles.hasIconLeft : ''
    } ${iconRight ? styles.hasIconRight : ''} ${className}`;

    // Build container classes
    const containerClasses = `${styles.container} ${isFullWidth ? styles.fullWidth : ''}`;

    return (
      <div className={containerClasses}>
        {label && (
          <label
            htmlFor={inputId}
            className={`${styles.label} ${isRequired ? styles.required : ''} ${
              disabled ? styles.disabled : ''
            }`}
          >
            {label}
            {isRequired && <span className={styles.required}> *</span>}
          </label>
        )}

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {iconLeft && <span className={`${styles.iconBase} ${styles.iconLeft}`}>{iconLeft}</span>}

          <input
            ref={ref}
            id={inputId}
            type={type}
            disabled={disabled}
            className={inputClasses}
            {...props}
          />

          {iconRight && <span className={`${styles.iconBase} ${styles.iconRight}`}>{iconRight}</span>}
        </div>

        {error && <p className={styles.errorText}>{error}</p>}

        {helperText && !error && <p className={styles.helperText}>{helperText}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
